
import { useLayoutEffect } from 'react';

const baseUrl = process.env.REACT_APP_BASE_URL;
export function formatCount(count) {
  if (count >= 1_000_000_000) {
    return (count / 1_000_000_000).toFixed(1) + 'b';
  } else if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(1) + 'm';
  } else if (count >= 1_000) {
    return (count / 1_000).toFixed(1) + 'k';
  } else {
    return count.toString();
  }
}

export const isPostEditable = (dateString) => {
  if (!dateString) return false;

  const gmtDate = new Date(dateString.endsWith("Z") ? dateString : `${dateString}Z`);
  if (isNaN(gmtDate.getTime())) return false;

  const now = new Date();
  const fiveMinutesInMs = 5 * 60 * 1000;
  const diffInMs = now - gmtDate;

  return diffInMs <= fiveMinutesInMs;
};

export const formatDateToRelativeOrShortDate = (dateString) => {
  if (!dateString) return "Invalid Date";

  // Ensure dateString is in a valid ISO 8601 format
  const isoDateString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;

  // Parse date safely
  const gmtDate = new Date(isoDateString);
  if (isNaN(gmtDate.getTime())) return "Invalid Date"; // Handle bad dates

  const localDate = new Date(gmtDate.getTime());
  const now = new Date();

  // Calculate time differences
  const diffInMs = now - localDate;
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  // Relative time formatting for past dates
  if (diffInSeconds < 60) return `${diffInSeconds} sec${diffInSeconds !== 1 ? "s" : ""} ago`;
  if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes !== 1 ? "s" : ""} ago`;
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  if (diffInDays < 5) return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;

  // Format as a short date using Intl API
  return localDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export async function fetchUsername(userId) {

  try {
    const res = await fetch(`${baseUrl}/php/get_username.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });

    const data = await res.json();
    if (data.success) {
      return data.username;
    } else {
      console.error('Failed to get full name:', data.message);
      return null;
    }
  } catch (err) {
    console.error('Error fetching full name:', err);
    return null;
  }
}

export default function useScrollLockOnMount() {
  // useLayoutEffect is preferred for DOM manipulations to prevent visual flicker.
  useLayoutEffect(() => {
    // --- Phase 1: Save current state before making changes ---

    // 1. Save the original body overflow style
    const originalOverflow = window.getComputedStyle(document.body).overflow;
    
    // 2. Save the original body paddingRight style
    const originalPaddingRight = document.body.style.paddingRight;

    // 3. Save the exact scroll position of the window
    const scrollY = window.scrollY;

    // --- Phase 2: Apply the lock ---

    // 4. Calculate scrollbar width to prevent the page content from shifting
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // 5. Apply the lock styles
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // --- Phase 3: Return the cleanup function ---
    // This function will be called automatically when the component unmounts.
    return () => {
      // 6. Restore the original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;

      // 7. CRITICAL: Restore the saved scroll position AFTER restoring the overflow style.
      // This is the key fix that prevents the page from jumping to the top.
      window.scrollTo(0, scrollY);
    };
  }, []); // The empty dependency array ensures this effect runs only once on mount and unmount.
}

  